import { useContext } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { QueryViewerContext } from "@softmax/web-features/data";
import type {
  StorePageEditConflict,
  StorePageEditResult,
} from "../store-page/types";
import { storePageQueryOptions } from "../store-page.queries";
import { emptyStorePageContent } from "../store-page/content-limits";
import {
  reconcileConflict,
  reconcileRevision,
} from "../store-page/reconcile-snapshot";

export function useStorePageSnapshot({
  slug,
  enabled,
}: {
  readonly slug: string;
  readonly enabled: boolean;
}) {
  const viewerId = useContext(QueryViewerContext);
  const client = useQueryClient();
  const options = storePageQueryOptions({ slug, viewerId });
  const query = useQuery({ ...options, enabled });
  const cancelRead = () => client.cancelQueries({ queryKey: options.queryKey });
  const refetchActive = () =>
    client.refetchQueries({
      queryKey: options.queryKey,
      exact: true,
      type: "active",
    });

  async function acceptConflict(conflict: StorePageEditConflict) {
    await cancelRead();
    const cachedDraft = client.getQueryData(options.queryKey)?.draft;
    const cachedContent = { ...emptyStorePageContent, ...cachedDraft?.content };
    client.setQueryData(options.queryKey, (previous) =>
      reconcileConflict({ previous, conflict }),
    );
    await client.invalidateQueries({
      queryKey: options.queryKey,
      refetchType: "none",
    });
    return {
      content: cachedContent,
      savedContent: cachedContent,
      baseRevisionId: cachedDraft?.current_revision_id ?? null,
    };
  }

  async function acceptRevision(result: StorePageEditResult) {
    await cancelRead();
    client.setQueryData(options.queryKey, (previous) =>
      reconcileRevision({ previous, result }),
    );
  }

  return { query, cancelRead, refetchActive, acceptConflict, acceptRevision };
}
